'use es6';

import { PureComponent } from 'react';
import styled from 'styled-components';
import I18n from 'I18n';
import { EERIE } from 'HubStyleTokens/colors';
import UIDescriptionList from 'UIComponents/list/UIDescriptionList';
import UISpinner from 'UIComponents/loading/UILoadingSpinner';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIBox from 'UIComponents/layout/UIBox';
import UICheckbox from 'UIComponents/input/UICheckbox';
import UICloseButton from 'UIComponents/button/UICloseButton';
import UILink from 'UIComponents/link/UILink';
import { View } from 'foundations-components/components/View';
import PerformanceStatsGatherer from 'ContentEditorUI/lib/PerformanceStatsGatherer';

/**
 * Migrated old styles in class into styled component for compliance with Trellis.
 * There were some custom UIComponent overrides in the old styles, which are not
 * overridden here as it is not compliant with Trellis. If we update this component to work
 * we should re-assess the UI on Foundational Components and apply overrides as needed.
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PerformanceCounterContainer = styled.div.withConfig({
  displayName: "PerformanceStatsOverlay__PerformanceCounterContainer",
  componentId: "idvx8v-0"
})(({
  theme
}) => {
  return {
    fontSize: theme.typography['detail-100'].fontSize,
    position: 'fixed',
    zIndex: 2,
    top: '20px',
    left: '20px',
    padding: theme.space['400'],
    backgroundColor: theme.color['fill-field-default']
  };
});
class PerformanceStatsList extends PureComponent {
  render() {
    const {
      stats
    } = this.props;
    if (!stats) {
      return null;
    }
    return /*#__PURE__*/_jsx(View, {
      pointerEvents: "none",
      children: /*#__PURE__*/_jsx(UIDescriptionList, {
        children: Object.keys(stats.counters).map(metricKey => {
          const count = stats.counters[metricKey];
          const calculations = stats.calculations[metricKey] || {};
          const hasAvg = calculations.avg != null;
          const hasLastValue = calculations.lastValue != null;
          const hasLastAvg = calculations.lastAvg != null;
          return [/*#__PURE__*/_jsx("dt", {
            children: metricKey
          }, `${metricKey}`), /*#__PURE__*/_jsxs("dd", {
            children: [count, "\xA0 \xA0", hasAvg ? ` avg ${I18n.formatNumber(calculations.avg)}ms` : '', "\xA0 \xA0", hasLastValue ? ` last value ${I18n.formatNumber(calculations.lastValue)}ms` : '', hasLastAvg && !hasLastValue ? ` last avg ${I18n.formatNumber(calculations.lastAvg)}ms` : '']
          }, `${metricKey}-value`)];
        })
      })
    });
  }
}
class PerformanceGatheringCheckboxes extends PureComponent {
  render() {
    const {
      isSetToProfileReact,
      isCurrentlyProfilingReact,
      onCheckAutomaticProfile,
      printWasted,
      printInclusive,
      printExclusive,
      printOperations,
      onCheckPrintWasted,
      onCheckPrintInclusive,
      onCheckPrintExclusive,
      onCheckPrintOperations
    } = this.props;
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsxs(UIFlex, {
        align: "center",
        justify: "center",
        children: [/*#__PURE__*/_jsx(UICheckbox, {
          checked: isSetToProfileReact,
          onChange: onCheckAutomaticProfile,
          size: "small",
          children: "Automatically profile React on hover"
        }), /*#__PURE__*/_jsx(View, {
          visibility: isCurrentlyProfilingReact ? '' : 'hidden',
          children: /*#__PURE__*/_jsx(UISpinner, {
            size: "extra-small"
          })
        })]
      }), /*#__PURE__*/_jsxs(UIFlex, {
        className: "m-left-5",
        children: [/*#__PURE__*/_jsx(UIBox, {
          grow: 1,
          children: /*#__PURE__*/_jsx(UICheckbox, {
            checked: printWasted,
            readOnly: !isSetToProfileReact,
            onChange: onCheckPrintWasted,
            size: "small",
            children: "printWasted()"
          })
        }), /*#__PURE__*/_jsx(UIBox, {
          grow: 1,
          children: /*#__PURE__*/_jsx(UICheckbox, {
            checked: printInclusive,
            readOnly: !isSetToProfileReact,
            onChange: onCheckPrintInclusive,
            size: "small",
            children: "printInclusive()"
          })
        })]
      }), /*#__PURE__*/_jsxs(UIFlex, {
        className: "m-left-5",
        children: [/*#__PURE__*/_jsx(UIBox, {
          grow: 1,
          children: /*#__PURE__*/_jsx(UICheckbox, {
            checked: printExclusive,
            readOnly: !isSetToProfileReact,
            onChange: onCheckPrintExclusive,
            size: "small",
            children: "printExclusive()"
          })
        }), /*#__PURE__*/_jsx(UIBox, {
          grow: 1,
          children: /*#__PURE__*/_jsx(UICheckbox, {
            checked: printOperations,
            readOnly: !isSetToProfileReact,
            onChange: onCheckPrintOperations,
            size: "small",
            children: "printOperations()"
          })
        })]
      })]
    });
  }
}
class PerformanceStatsOverlay extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {};
    this.perfGatherer = PerformanceStatsGatherer.getSingletonInstance();
    this.onCheckPrintWasted = this.makeOnCheckReactPerfMethod('printWasted');
    this.onCheckPrintExclusive = this.makeOnCheckReactPerfMethod('printExclusive');
    this.onCheckPrintInclusive = this.makeOnCheckReactPerfMethod('printInclusive');
    this.onCheckPrintOperations = this.makeOnCheckReactPerfMethod('printOperations');
    this.onPerfGathererUpdate = this.onPerfGathererUpdate.bind(this);
    this.onCheckAutomaticProfile = this.onCheckAutomaticProfile.bind(this);
    this.onClickLogLink = this.onClickLogLink.bind(this);
  }
  componentDidMount() {
    if (PerformanceStatsGatherer.isGatheringStats()) {
      PerformanceStatsGatherer.getSingletonInstance().subscribe(this.onPerfGathererUpdate);
    }
  }
  componentWillUnmount() {
    if (PerformanceStatsGatherer.isGatheringStats()) {
      PerformanceStatsGatherer.getSingletonInstance().unsubscribe(this.onPerfGathererUpdate);
    }
  }
  onPerfGathererUpdate({
    isFor
  } = {}) {
    if (isFor === 'meta') {
      const isSetToProfileReact = this.perfGatherer.isSetToProfileReact();
      const isCurrentlyProfilingReact = this.perfGatherer.isCurrentlyProfilingReact();
      const {
        printWasted,
        printInclusive,
        printExclusive,
        printOperations
      } = this.perfGatherer.getReactPerfMethodsToCall();
      this.setState({
        isSetToProfileReact,
        isCurrentlyProfilingReact,
        printWasted,
        printInclusive,
        printExclusive,
        printOperations
      });
    } else {
      this.setState({
        latestStatsTimestamp: new Date()
      });
    }
  }
  onCheckAutomaticProfile({
    target: {
      checked
    }
  }) {
    this.perfGatherer.changeIsSetToProfileReact(checked);
  }
  makeOnCheckReactPerfMethod(methodName) {
    return event => this.onCheckReactPerfMethod(methodName, event);
  }
  onCheckReactPerfMethod(methodName, {
    target: {
      checked
    }
  }) {
    this.perfGatherer.setReactPerfMethodsToCall({
      [methodName]: checked
    });
  }
  onClickLogLink() {
    const statsClone = JSON.parse(JSON.stringify(window.hs.perfGatherer.getStats()));
    console.log('All D&D perf stats:', statsClone);
  }
  render() {
    if (!PerformanceStatsGatherer.isGatheringStats()) {
      return null;
    }
    const {
      onClickClose
    } = this.props;
    const {
      overlayHidden,
      latestStatsTimestamp,
      isSetToProfileReact,
      isCurrentlyProfilingReact,
      printWasted,
      printInclusive,
      printExclusive,
      printOperations
    } = this.state;
    if (overlayHidden) {
      return null;
    }
    return /*#__PURE__*/_jsxs(PerformanceCounterContainer, {
      children: [/*#__PURE__*/_jsx(UICloseButton, {
        color: EERIE,
        onClick: onClickClose,
        size: "small"
      }), /*#__PURE__*/_jsx(PerformanceStatsList, {
        stats: this.perfGatherer.getStats({
          forceFlushCalcuations: false
        }),
        latestStatsTimestamp: latestStatsTimestamp
      }), /*#__PURE__*/_jsx(PerformanceGatheringCheckboxes, {
        isSetToProfileReact: isSetToProfileReact,
        isCurrentlyProfilingReact: isCurrentlyProfilingReact,
        onCheckAutomaticProfile: this.onCheckAutomaticProfile,
        printWasted: printWasted,
        printInclusive: printInclusive,
        printExclusive: printExclusive,
        printOperations: printOperations,
        onCheckPrintWasted: this.onCheckPrintWasted,
        onCheckPrintInclusive: this.onCheckPrintInclusive,
        onCheckPrintExclusive: this.onCheckPrintExclusive,
        onCheckPrintOperations: this.onCheckPrintOperations
      }), /*#__PURE__*/_jsx("div", {
        className: "m-top-2",
        children: /*#__PURE__*/_jsx(UILink, {
          onClick: this.onClickLogLink,
          children: "Log all current stats"
        })
      })]
    });
  }
}

// Having a wrapper with hidden state, so that unmounting PerformanceStatsOverlay will automatically
// unsubscribe from perf gatherer updates
export default class PerformanceStatsOverlayWrapper extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {};
    this.onClickClose = this.onClickClose.bind(this);
  }
  onClickClose() {
    console.warn(`Closing the overlay, but still debugging. To turn off debugging you need to run \`hubspot.ContentEditorUI.devHelpers.setDnDPerfDebugFlag(false)\` in the parent.

So you can continue to access the stats by calling \`window.hs.perfGatherer.getStats()\` in the iframe`);
    console.log('\nhs.perfGatherer reference:', window.hs.perfGatherer, '\n');
    this.setState({
      overlayHidden: true
    });
  }
  render() {
    const {
      overlayHidden
    } = this.state;
    if (overlayHidden) {
      return null;
    }
    return /*#__PURE__*/_jsx(PerformanceStatsOverlay, {
      onClickClose: this.onClickClose
    });
  }
}